/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEmpty } from '../common/isEmpty';
import isObject from '../common/isObject';
import { isIos, isInApp, isNaiYou } from './DtPlatform';

interface ExtendedWindow extends Window {
  WebViewJavascriptBridge: any;
  WVJBCallbacks: any[];
}

declare const window: ExtendedWindow;

interface GlobalBridge {
  callHandler: (
    eventName: string,
    dataString: string,
    callback: (response: Record<string, any> | string) => void,
  ) => void;
}

let globalBridge: GlobalBridge;

type SdkFunction = (data: any, callback?: any) => void;

interface DtSdkType {
  isInit: boolean;
  init: () => void;
  VERSION: number;
  sdkVersion: () => number;
  cacheData: Record<string, any>;
  ready: (callback: () => void) => void;
  responseNotification:
    | ((params: { name: string; data?: any; [key: string]: any }) => void)[]
    | ((params: Record<string, any>) => void);
  responseNavigationClick: (params?: Record<string, any>) => void;
  responseNavigationButtonsClick: (params?: Record<string, any>) => void;
  notification: SdkFunction;
  closeUrl: SdkFunction;
  openUrl: SdkFunction;
  alipay: SdkFunction;
  isLogin: SdkFunction;
  login: SdkFunction;
  setNavigation: SdkFunction;
  trc: SdkFunction;
  showAlertInfoView: SdkFunction;
  showAlertView: SdkFunction;
  socialShare: SdkFunction;
  blogComment: SdkFunction;
  blogZan: SdkFunction;
  blogLike: SdkFunction;
  blogForward: SdkFunction;
  blogCreate: SdkFunction;
  selectionAlbumFeedback: SdkFunction;
  photoBrowse: SdkFunction;
  albumChoose: SdkFunction;
  titleChange: SdkFunction;
  httpRequest: SdkFunction;
  wxpay: SdkFunction;
  dtraceLog: SdkFunction;
  getDeviceInfo: SdkFunction;
  setHandleBackButton: SdkFunction;
  getAddress: SdkFunction;
  musicAction: SdkFunction;
  commentMoreAction: SdkFunction;
  responseCheckInfo: SdkFunction;
  showHtmlDialog: SdkFunction;
  hideHtmlDialog: SdkFunction;
  setNavigationButtons: SdkFunction;
  webPageDidLoad: SdkFunction;
  videoAction: SdkFunction;
  dismissFullCardView: SdkFunction;
  startRecording: SdkFunction;
  stopRecording: SdkFunction;
  topVideoAction: SdkFunction;
  setTopVideoState: SdkFunction;
  popKeyboard: SdkFunction;
  closeKeyboard: SdkFunction;
  commentLongClick: SdkFunction;
  setShopSearchKeyword: SdkFunction;
  getTopVideoInfo: SdkFunction;
  dtraceEventSpreadStart: SdkFunction;
  dtraceEventSpreadEnd: SdkFunction;
  setRecordSettings: SdkFunction;
  setScrollPosition: SdkFunction;
  dismissHomePromotionPage: SdkFunction;
  getNetworkInfo: SdkFunction;
  getAppLocation: SdkFunction;
  keyboardUp: SdkFunction;
  asyncTaskInspire: SdkFunction;
  setDataToNative: SdkFunction;
  getDataFromNative: SdkFunction;
  viewVideoAd: SdkFunction;
  viewPopBannerAd: SdkFunction;
  dismissPopBannerAd: SdkFunction;
  wechatAuthorization: SdkFunction;
  imagesUpload: SdkFunction;
  mediaDownload: SdkFunction;
  tiktokShare: SdkFunction;
  widgetConfig: SdkFunction;
  applePay: SdkFunction;
  alipaySingle: SdkFunction;
  payment: SdkFunction;
  dtrace: SdkFunction;
  getTouristInfo: SdkFunction;
  useTemplate: SdkFunction;
  imageBase64sWithUrlsUpload: SdkFunction;
  getAuth: SdkFunction;
  getTeenModeInfo: SdkFunction;
  findPasswordByPhoneNumber: SdkFunction;
  openWatermarkEditor: SdkFunction;
  getDeviceInfoStr: SdkFunction;
}

const DtSdk: DtSdkType = {} as any;

DtSdk.isInit = false;

// Native call js function start
function _callHandler(
  method: string,
  data: Record<string, any>,
  callback?: (jsn: string) => void,
): void {
  if (typeof data !== 'object') {
    if (typeof callback !== 'function') {
      callback = data;
      data = {
        method: method,
      };
    }
  } else {
    if (isEmpty(data)) {
      data = {
        method: method,
      };
    } else {
      const cacheData = data;
      data = {
        method: method,
        params: cacheData,
      };
    }
  }
  const dataString = JSON.stringify(data);
  if (globalBridge) {
    globalBridge.callHandler('duitangSDKHandler', dataString, function (response) {
      const jsn = isObject(response) ? response : JSON.parse(response as string);
      if (typeof callback === 'function') {
        callback(jsn);
      }
    });
  } else {
    console.log('sdk is not ready');
  }
}
// Native call js function end

const sdkVersion = (): number => {
  const ua = typeof window !== 'undefined' ? window.navigator.userAgent.toLowerCase() : '';
  // eslint-disable-next-line no-useless-escape
  const jssdk = /(jssdk)[ \/]([\w.]+)/;
  const match = jssdk.exec(ua);
  let version;
  if (match === null) {
    version = '0';
    return parseFloat(version);
  } else {
    version = match[2] || '0.1';
    return parseFloat(version);
  }
};
DtSdk.VERSION = /*@__PURE__*/ sdkVersion();
DtSdk.sdkVersion = sdkVersion;
DtSdk.cacheData = {};

interface Bridge extends GlobalBridge {
  init: (
    callback: (
      message: string,
      responseCallback: (data: { 'Javascript Responds': string }) => void,
    ) => void,
  ) => void;
  registerHandler: (
    eventName: string,
    callback: (response: { method: string; params: Record<string, any> } | string) => void,
  ) => void;
}

// jssdk 3.0 开始 iOS 使用 WKWebview 替代 UIWebview
function connectWebViewJavascriptBridge(callback: (bridge: Bridge) => void): number | void {
  if (DtSdk.VERSION >= 3.0 && isIos()) {
    if (window.WebViewJavascriptBridge) {
      return callback(window.WebViewJavascriptBridge);
    }
    if (window.WVJBCallbacks) {
      return window.WVJBCallbacks.push(callback);
    }
    window.WVJBCallbacks = [callback];
    const WVJBIframe = document.createElement('iframe');
    WVJBIframe.style.display = 'none';
    WVJBIframe.src = 'https://__bridge_loaded__';
    document.documentElement.appendChild(WVJBIframe);
    setTimeout(function () {
      document.documentElement.removeChild(WVJBIframe);
    }, 0);
  } else if (window.WebViewJavascriptBridge) {
    callback(window.WebViewJavascriptBridge);
  } else {
    document.addEventListener(
      'WebViewJavascriptBridgeReady',
      function () {
        if (window.WebViewJavascriptBridge) {
          callback(window.WebViewJavascriptBridge);
        } else {
          console.error('WebViewJavascriptBridge is not defined');
        }
      },
      false,
    );
  }
}

DtSdk.init = function () {
  if (DtSdk.isInit) {
    return;
  }
  DtSdk.isInit = true;
  connectWebViewJavascriptBridge(function (bridge) {
    globalBridge = bridge;
    if (!(DtSdk.VERSION >= 3.0 && isIos())) {
      bridge.init(function (_message, responseCallback) {
        const data = {
          'Javascript Responds': 'init!',
        };
        responseCallback(data);
      });
    }
    bridge.registerHandler('_callNavtiveHandler', function (response) {
      const jsn = isObject(response) ? response : JSON.parse(response as string);
      const method = jsn.method || '';
      const params = jsn.params || {};
      switch (method) {
        case 'init':
          DtSdk.cacheData = params.data || {};
          break;
        case 'postNotification':
          if (typeof DtSdk.responseNotification === 'function') {
            DtSdk.responseNotification(params);
          } else if (Array.isArray(DtSdk.responseNotification)) {
            for (let i = (DtSdk.responseNotification as any[]).length - 1; i >= 0; i--) {
              (DtSdk.responseNotification as any[])[i](params);
            }
          }
          break;
        case 'onNavigationBarRightButtonClick':
          DtSdk.responseNavigationClick(params);
          break;
        //1.2 version start
        case 'onNavigationBarButtonsClick':
          DtSdk.responseNavigationButtonsClick(params);
          break;
        //1.2 version end
        default:
      }
    });
  });
};

// sdk ready, 所有方法写在ready内部
DtSdk.ready = function (callback) {
  if (!DtSdk.isInit) {
    console.error('You must call DtSdk.init first');
    return;
  }
  if (isInApp()) {
    connectWebViewJavascriptBridge(callback);
  } else {
    callback();
  }
};

DtSdk.notification = function (data, callback) {
  _callHandler('postNotification', data, callback);
};
/*
描述：调用关闭url
参数：data
{
  "type": 'closeurl'
}
*/
DtSdk.closeUrl = function (data, callback) {
  _callHandler('closeURL', data, callback);
};

/*
描述：调用原生webview前进方式
参数：data
{
  "type": 'present||push'
}
参数：
*/
DtSdk.openUrl = function (data, callback) {
  if (isInApp()) {
    // eslint-disable-next-line no-useless-escape
    const reg = /^[a-zA-Z\d_-]{1,20}\:\/\//i;
    const domain = window.location.origin;
    if (!reg.test(data.url)) data.url = domain + data.url;
    _callHandler('openURL', data, callback);
  } else {
    window.location.href = data.url;
  }
};

/*
描述：调用原生支付宝
参数：data
{
  "order_string": ''
}
*/
DtSdk.alipay = function (data, callback) {
  _callHandler('alipay', data, callback);
};
/*
描述：调用原生登陆
参数：data
{} || 不传参
*/
DtSdk.isLogin = function (data, callback) {
  _callHandler('checkLogin', data, callback);
};

DtSdk.login = function (data, callback) {
  _callHandler('login', data, callback);
};
DtSdk.setNavigation = function (data, callback) {
  _callHandler('setNavigationBarRightButton', data, callback);
};

DtSdk.trc = function (data, callback) {
  const param = data.split(',');
  if (param.length >= 3) {
    const name = param[0];
    const attrkey = param[1];
    const attrvalue = param.slice(2).join(',');
    const attributes: Record<string, any> = {};
    if (attrkey !== undefined) {
      attributes[attrkey] = attrvalue;
    }
    data = {
      name: name,
      attributes: attributes,
    };
    _callHandler('dtraceEvent', data, callback);
  }
};

// 0.3 version start
DtSdk.showAlertInfoView = function (data, callback) {
  _callHandler('showAlertInfoView', data, callback);
};

DtSdk.showAlertView = function (data, callback) {
  _callHandler('showAlertView', data, callback);
};

DtSdk.socialShare = function (data, callback) {
  _callHandler('socialShare', data, callback);
};

DtSdk.blogComment = function (data, callback) {
  _callHandler('blogComment', data, callback);
};

DtSdk.blogZan = function (data, callback) {
  _callHandler('blogZan', data, callback);
};

DtSdk.blogLike = function (data, callback) {
  _callHandler('blogLike', data, callback);
};

DtSdk.blogForward = function (data, callback) {
  _callHandler('blogForward', data, callback);
};

// 0.3 version end

// 0.4 version start
DtSdk.blogCreate = function (data, callback) {
  _callHandler('blogCreate', data, callback);
};

DtSdk.selectionAlbumFeedback = function (data, callback) {
  _callHandler('selectionAlbumFeedback', data, callback);
};
// 0.4 version end

// 0.5 version start
DtSdk.photoBrowse = function (data, callback) {
  _callHandler('photoBrowse', data, callback);
};
DtSdk.albumChoose = function (data, callback) {
  _callHandler('albumChoose', data, callback);
};
DtSdk.titleChange = function (data, callback) {
  _callHandler('titleChange', data, callback);
};
// 0.5 version end

// 0.6 version start
DtSdk.httpRequest = function (data, callback) {
  _callHandler('httpRequest', data, callback);
};
// 0.6 version end

// 0.7 version start 客户端5.9
DtSdk.wxpay = function (data, callback) {
  _callHandler('wxpay', data, callback);
};
// 0.7 version end

// 0.9 version start
DtSdk.dtraceLog = function (data, callback) {
  _callHandler('dtraceLog', data, callback);
};
// 0.9 version end
// 1.0 version start 客户端6.0
DtSdk.getDeviceInfo = function (data, callback) {
  _callHandler('getDeviceInfo', data, callback);
};
DtSdk.setHandleBackButton = function (data, callback) {
  _callHandler('setHandleBackButton', data, callback);
};
DtSdk.getAddress = function (data, callback) {
  _callHandler('getAddress', data, callback);
};
// 1.0 version end
// 1.1 version start
// 客户端版本 6.2.1 IOS
DtSdk.musicAction = function (data, callback) {
  _callHandler('musicAction', data, callback);
};

DtSdk.commentMoreAction = function (data, callback) {
  _callHandler('commentMoreAction', data, callback);
};
//
DtSdk.responseCheckInfo = function (data, callback) {
  _callHandler('responseCheckInfo', data, callback);
};
// 1.1 version end

//1.2 version start
// 客户端版本 ios 6.2.2 ; andriod 6.2.6(84)
// 弹出html 弹窗
DtSdk.showHtmlDialog = function (data, callback) {
  _callHandler('showHtmlDialog', data, callback);
};
// 关闭html 弹窗
DtSdk.hideHtmlDialog = function (data, callback) {
  _callHandler('hideHtmlDialog', data, callback);
};
DtSdk.setNavigationButtons = function (data, callback) {
  _callHandler('setNavigationBarButtons', data, callback);
};
//1.2 version end

// 1.3 version start iOS
DtSdk.webPageDidLoad = function (data, callback) {
  _callHandler('webPageDidLoad', data, callback);
};
// 1.3 version end

// 1.4 version
DtSdk.videoAction = function (data, callback) {
  _callHandler('videoAction', data, callback);
};
// 1.4 version end

DtSdk.responseNotification = [] as any;

//1.2 version start
// eslint-disable-next-line @typescript-eslint/no-empty-function
DtSdk.responseNavigationButtonsClick = function () {};
//1.2 version end

//1.5 version start
DtSdk.dismissFullCardView = function (data, callback) {
  _callHandler('dismissFullCardView', data, callback);
};
//1.5 version end

//1.6 version start
if (DtSdk.VERSION >= 1.6) {
  DtSdk.startRecording = function (data, callback) {
    _callHandler('startRecording', data, callback);
  };
  DtSdk.stopRecording = function (data, callback) {
    _callHandler('stopRecording', data, callback);
  };
  DtSdk.topVideoAction = function (data, callback) {
    _callHandler('topVideoAction', data, callback);
  };
}
//1.6 version end

//1.7 version start
if (DtSdk.VERSION >= 1.7) {
  DtSdk.setTopVideoState = function (data, callback) {
    _callHandler('setTopVideoState', data, callback);
  };

  DtSdk.popKeyboard = function (data, callback) {
    _callHandler('popKeyboard', data, callback);
  };

  // Android only  iOS 原生应用接管处理了键盘close
  DtSdk.closeKeyboard = function (data, callback) {
    _callHandler('closeKeyboard', data, callback);
  };

  DtSdk.commentLongClick = function (data, callback) {
    _callHandler('commentLongClick', data, callback);
  };

  DtSdk.setShopSearchKeyword = function (data, callback) {
    _callHandler('setShopSearchKeyword', data, callback);
  };
}
//1.7 version end

//1.8 version start
if (DtSdk.VERSION >= 1.8) {
  DtSdk.getTopVideoInfo = function (data, callback) {
    _callHandler('getTopVideoInfo', data, callback);
  };
}
//1.8 version end

//1.9 version start
if (DtSdk.VERSION >= 1.9) {
  DtSdk.dtraceEventSpreadStart = function (data, callback) {
    _callHandler('dtraceEventSpreadStart', data, callback);
  };
  DtSdk.dtraceEventSpreadEnd = function (data, callback) {
    _callHandler('dtraceEventSpreadEnd', data, callback);
  };
  DtSdk.setRecordSettings = function (data, callback) {
    _callHandler('setRecordSettings', data, callback);
  };
}
//1.9 version end

//2.0 version start
if (DtSdk.VERSION >= 2.0) {
  DtSdk.setScrollPosition = function (data, callback) {
    _callHandler('setScrollPosition', data, callback);
  };
}
//2.0 version end

//2.1 version start
if (DtSdk.VERSION >= 2.1) {
  DtSdk.dismissHomePromotionPage = function (data, callback) {
    _callHandler('dismissHomePromotionPage', data, callback);
  };
}
//2.1 version end

//2.4 version start
if (DtSdk.VERSION >= 2.4) {
  DtSdk.getNetworkInfo = function (data, callback) {
    _callHandler('getNetworkInfo', data, callback);
  };

  DtSdk.getAppLocation = function (data, callback) {
    _callHandler('getAppLocation', data, callback);
  };
}
//2.4 version end

//2.5 version start
if (DtSdk.VERSION >= 2.5) {
  DtSdk.keyboardUp = function (data, callback) {
    _callHandler('keyboardUp', data, callback);
  };
}
//2.5 version end

//2.6 version start
if (DtSdk.VERSION >= 2.6) {
  DtSdk.asyncTaskInspire = function (data, callback) {
    _callHandler('asyncTaskInspire', data, callback);
  };
  DtSdk.setDataToNative = function (data, callback) {
    _callHandler('setDataToNative', data, callback);
  };
  DtSdk.getDataFromNative = function (data, callback) {
    _callHandler('getDataFromNative', data, callback);
  };
}

//2.6 version end

//3.0 version start
if (DtSdk.VERSION >= 3.0) {
  DtSdk.viewVideoAd = function (data, callback) {
    _callHandler('viewVideoAd', data, callback);
  };

  DtSdk.viewPopBannerAd = function (data, callback) {
    _callHandler('viewPopBannerAd', data, callback);
  };

  DtSdk.dismissPopBannerAd = function (data, callback) {
    _callHandler('dismissPopBannerAd', data, callback);
  };
}
//3.0 version end

//3.1 version start
if (DtSdk.VERSION >= 3.1) {
  DtSdk.wechatAuthorization = function (data, callback) {
    _callHandler('wechatAuthorization', data, callback);
  };
}
//3.1 version end

//3.2 version start
if (DtSdk.VERSION >= 3.2) {
  DtSdk.imagesUpload = function (data, callback) {
    _callHandler('imagesUpload', data, callback);
  };
  DtSdk.mediaDownload = function (data, callback) {
    _callHandler('mediaDownload', data, callback);
  };
}
//3.2 version end

if (DtSdk.VERSION >= 3.3) {
  DtSdk.tiktokShare = function (data, callback) {
    _callHandler('tiktokShare', data, callback);
  };
}

//3.4 version start
if (DtSdk.VERSION >= 3.4) {
  // ios only
  DtSdk.widgetConfig = function (data, callback) {
    _callHandler('widgetConfig', data, callback);
  };
}

// 3.5 naiyou start
if (DtSdk.VERSION >= 3.5 && isNaiYou()) {
  DtSdk.getAuth = function (data, callback) {
    _callHandler('getAuth', data, callback);
  };
}

//3.7 version start
if (DtSdk.VERSION >= 3.7) {
  // ios only
  DtSdk.applePay = function (data, callback) {
    _callHandler('applePay', data, callback);
  };
  // android only
  DtSdk.alipaySingle = function (data, callback) {
    _callHandler('alipaySingle', data, callback);
  };
}

//3.8 version start
if (DtSdk.VERSION >= 3.8) {
  DtSdk.payment = function (data, callback) {
    _callHandler('payment', data, callback);
  };
}

//3.9 version start
if (DtSdk.VERSION >= 3.9) {
  /**
   * @description 调用客户端的数数打点
   * @param {{name: string, attributes: object<any>, batch_list: object<any>[]}} data name是事件名称、attributes对象中的value只可以是字符串、数字、布朗值、字符串数组、batch_list对象数组中的对象值只能是字符串、数字、布朗值
   * @param {undefined} callback 不需要回调
   */
  DtSdk.dtrace = function (
    data: { name: string; attributes: Record<string, any>; batch_list: Record<string, any>[] },
    callback,
  ) {
    _callHandler('dtrace', data, callback);
  };
}

if (DtSdk.VERSION >= 4.1) {
  DtSdk.getTouristInfo = function (data: any, callback) {
    _callHandler('getTouristInfo', data, callback);
  };
}

// 4.2 version start
if (DtSdk.VERSION >= 4.2) {
  // data: { template_id: string }
  // callback参数: { status: number }，1代表成功
  DtSdk.useTemplate = function (data, callback) {
    _callHandler('useTemplate', data, callback);
  };

  /**
  * 
  * @param 不需要参数 
  * @description 返回值 {
    "status": 1, // 1表示成功，其它表示失败
    "message": nil
    "data": { "imageUrlsList": ["url_0", "url_1", "url_2"],  // 图片url
              "imageBase64List": ["base64_0","","base64_2"],  // 图片base64
            }
  }
  */
  DtSdk.imageBase64sWithUrlsUpload = function (data, callback) {
    _callHandler('imageBase64sWithUrlsUpload', data, callback);
  };
}

// 4.3 version start
if (DtSdk.VERSION >= 4.3) {
  DtSdk.getTeenModeInfo = function (data, callback) {
    _callHandler('getTeenModeInfo', data, callback);
  };
}

if (DtSdk.VERSION >= 4.3 && isNaiYou()) {
  DtSdk.getDeviceInfoStr = function (data, callback) {
    _callHandler('getDeviceInfoStr', data, callback);
  };
}

// 4.3 version start
if (DtSdk.VERSION >= 4.4) {
  DtSdk.findPasswordByPhoneNumber = function (data, callback) {
    _callHandler('findPasswordByPhoneNumber', data, callback);
  };
}

// 4.5 version start
if (DtSdk.VERSION >= 4.5) {
  DtSdk.openWatermarkEditor = function (data, callback) {
    _callHandler('openWatermarkEditor', data, callback);
  };
}

export default DtSdk;
